// Import utility functions
import { FormatCurrency } from "global/utilities/ConvertCurrency";

// Import CSS
import "./RefundBreakdown.css";



// This displays the financial calculation behind an order refund
const RefundBreakdown = ({ refundBreakdown, transaction }) => {

    // This stores the refund values safely
    const refund = refundBreakdown?.refund || {};

    // This stores the calculation values safely
    const calculation = refundBreakdown?.calculation || {};

    // This stores the resulting balance transaction safely
    const result = refundBreakdown?.result || {};

    // Keep the original customer payment currency separate from the account currency
    const refundCurrency = refund?.currency;
    const currency = transaction?.currency || result?.currency;
    const isCrossCurrency = refundCurrency !== currency;

    // This stores the account-currency refund amount calculated from the original earning
    const convertedRefund = Number(refundBreakdown?.converted_refund?.amount ?? 0) / 100;

    // This stores any separately credited import charges or balance adjustments
    const importCredit = Number(refundBreakdown?.import_charges_credit?.amount ?? 0) / 100;
    const balanceAdjustment = Number(refundBreakdown?.balance_adjustment?.amount ?? 0) / 100;

    // This stores the gross amount refunded to the customer
    const grossRefund = Number(refundBreakdown?.inc_tax ?? refund?.gross_major ?? 0);

    // This stores the recoverable tax amount
    const recoverableTax = Number(refundBreakdown?.tax ?? 0);

    // This stores the actual balance movement in major units
    const balanceMovement = Number(transaction?.amount ?? result?.amount ?? 0) / 100;

    // This stores whether the refund relates to a cancelled order
    const isCancelledOrder = calculation?.is_cancelled_order === true;

    // This stores whether production had already started
    const productionStarted = calculation?.production_started === true;

    // This stores whether the cancellation happened before production
    const isUnproducedCancellation = calculation?.is_unproduced_cancellation === true;

    // Read the fulfilment credit recorded against this refund
    const fulfilmentCredit = Number(refundBreakdown?.fulfilment_credit?.amount ?? 0) / 100;


    // Do not render the breakdown if no currency is available
    if (!currency || !refundCurrency) {

        // Return no component
        return null;

    }



    // This renders the refund breakdown
    return (

        // This is the main refund breakdown wrapper
        <div className="refund-breakdown">

            {/* This is the refund breakdown header */}
            <div className="refund-breakdown__header">

                {/* This is the refund breakdown title */}
                <h3 className="refund-breakdown__title">
                    Refund breakdown
                </h3>

                {/* This explains the balance breakdown */}
                <p className="refund-breakdown__subtitle">
                    A detailed breakdown of how the refund affects your balance.
                </p>

            </div>

            {/* This contains the refund calculation steps */}
            <div className="refund-breakdown__steps">

                {/* This shows the total amount refunded to the customer */}
                <div className="refund-breakdown__step refund-breakdown__step--negative">

                    {/* This is the gross refund label */}
                    <span className="refund-breakdown__label">
                        Customer refund
                    </span>

                    {/* This is the gross refund amount */}
                    <strong className="refund-breakdown__value">
                        -
                        {FormatCurrency(
                            refundCurrency,
                            grossRefund,
                            false
                        )}
                    </strong>

                </div>

                {/* This shows the tax recovered from the refund */}
                <div className="refund-breakdown__step refund-breakdown__step--positive">

                    {/* This is the recoverable tax label */}
                    <span className="refund-breakdown__label">
                        Recoverable tax
                    </span>

                    {/* This is the recoverable tax amount */}
                    <strong className="refund-breakdown__value">
                        +
                        {FormatCurrency(
                            refundCurrency,
                            recoverableTax,
                            false
                        )}
                    </strong>

                </div>

                {/* This separates the original customer refund from its account-currency equivalent */}
                {isCrossCurrency &&
                    <div className="refund-breakdown__step refund-breakdown__step--negative">
                        <span className="refund-breakdown__label">Refund excluding tax (account currency)</span>
                        <strong className="refund-breakdown__value">-{FormatCurrency(currency, convertedRefund, false)}</strong>
                    </div>
                }

                {/* This shows the unused fulfilment charge credited on cancellation */}
                {isUnproducedCancellation && fulfilmentCredit !== 0 &&
                    <div className={`refund-breakdown__step ${fulfilmentCredit > 0 ? "refund-breakdown__step--positive" : "refund-breakdown__step--negative"}`}>
                        <span className="refund-breakdown__label">Fulfilment charge returned</span>
                        <strong className="refund-breakdown__value">
                            {fulfilmentCredit > 0 ? "+" : ""}{FormatCurrency(currency, fulfilmentCredit, false)}
                        </strong>
                    </div>
                }

                {/* This shows the reversal of import charges that were not part of seller earnings */}
                {importCredit !== 0 &&
                    <div className="refund-breakdown__step refund-breakdown__step--positive">
                        <span className="refund-breakdown__label">Import charges returned</span>
                        <strong className="refund-breakdown__value">+{FormatCurrency(currency, importCredit, false)}</strong>
                    </div>
                }

                {/* This separately discloses any additional recorded order-balance adjustment */}
                {balanceAdjustment !== 0 &&
                    <div className={`refund-breakdown__step ${balanceAdjustment > 0 ? "refund-breakdown__step--positive" : "refund-breakdown__step--negative"}`}>
                        <span className="refund-breakdown__label">Additional balance adjustment</span>
                        <strong className="refund-breakdown__value">{balanceAdjustment > 0 ? "+" : ""}{FormatCurrency(currency, balanceAdjustment, false)}</strong>
                    </div>
                }

            </div>

            {/* This shows useful cancellation information when available */}
            {(isCancelledOrder || isUnproducedCancellation) &&

                // This contains the cancellation status message
                <div className="refund-breakdown__notice">

                    {/* This is the cancellation status title */}
                    <strong className="refund-breakdown__notice-title">
                        Cancellation refund
                    </strong>

                    {/* This explains the production state */}
                    <span className="refund-breakdown__notice-text">
                        {productionStarted
                            ? "Production had already started when this refund was processed."
                            : "Production had not started when this refund was processed."}
                    </span>

                </div>

            }

            {/* This shows the actual balance transaction amount */}
            <div className="refund-breakdown__total">
                <span className="refund-breakdown__total-label">
                    {balanceMovement < 0 ? "Deducted from your balance" : "Credited to your balance"}
                </span>
                <strong className="refund-breakdown__total-value">
                    {FormatCurrency(currency, balanceMovement, false)}
                </strong>
            </div>

        </div>

    );

};

// Export the refund breakdown component
export default RefundBreakdown;